import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import MainComponentWrapper from "../../Components/Common/MainComponentWrapper";
import GuardLayout from "../../Components/Layout/GuardLayout";
import Attendee from "../../Components/OrderMgt/Attendee/Attendee";
import useHttpHook from "../../Hooks/HttpHook";
import {
  GetAttendeeListForGuard,
  GetEventsToGuards,
} from "../../utils/ApiConstant";
import { createObjectForPaginatedResult } from "../../utils/Helper";
import { showErrorToast } from "../../utils/notify";

const toId = (value) => (value == null ? "" : String(value));

const GuardEventPage = () => {
  const [loading, sendRequest, error] = useHttpHook();
  const { token } = useSelector((state) => state.guard);
  const [attendeeList, setAttendeeList] = useState(null);
  const [totalAttendeesCount, setTotalAtteneesCount] = useState(0);
  const [events, setEvents] = useState([]);
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const eventFromUrl = searchParams.get("event") || "";
  const [selectedEvent, setSelectedEvent] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const goToGuardLogin = useCallback(() => {
    navigate("/guard/login", { replace: true });
  }, [navigate]);

  const assignedEventIds = useMemo(
    () => new Set(events.map((ev) => toId(ev._id)).filter(Boolean)),
    [events]
  );

  // Debounce search input so we don't hit the API on every keystroke.
  useEffect(() => {
    const delay = searchInput === "" ? 0 : 400;
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, delay);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const getList = useCallback(
    async (page = 1, eventId = selectedEvent, q = debouncedSearch) => {
      if (!token) {
        goToGuardLogin();
        return;
      }
      try {
        const params = new URLSearchParams({ page: String(page) });
        if (q && q.length > 0) {
          params.set("q", q);
        }
        if (eventId) {
          params.set("event", eventId);
        }
        const response = await sendRequest(
          `${GetAttendeeListForGuard}?${params.toString()}`,
          {
            method: "get",
            guardToken: token,
          }
        );
        const total =
          response?.data?.totalAttendees ??
          response?.data?.paginator?.itemCount ??
          0;
        setTotalAtteneesCount(total);
        if (page === 1) {
          setAttendeeList(response);
        } else {
          setAttendeeList((prevState) =>
            createObjectForPaginatedResult(response, prevState, "paginate")
          );
        }
      } catch (fetchError) {
        console.log(fetchError);
      }
    },
    [token, selectedEvent, debouncedSearch, sendRequest, goToGuardLogin]
  );

  const loadAssignedEvents = useCallback(async () => {
    if (!token) {
      return;
    }
    try {
      const response = await sendRequest(
        `${GetEventsToGuards}?includePast=true`,
        {
          method: "get",
          guardToken: token,
        }
      );
      const list = response?.data;
      if (Array.isArray(list)) {
        setEvents(list);
      } else if (Array.isArray(list?.itemsList)) {
        setEvents(list.itemsList);
      } else {
        setEvents([]);
      }
    } catch (fetchError) {
      console.log(fetchError);
      setEvents([]);
    } finally {
      setEventsLoaded(true);
    }
  }, [token, sendRequest]);

  useEffect(() => {
    if (!token) {
      goToGuardLogin();
      return;
    }
    setEventsLoaded(false);
    loadAssignedEvents();
  }, [token, loadAssignedEvents, goToGuardLogin]);

  // Resolve URL event only after assigned events are loaded, so we never
  // query an event this guard is not assigned to.
  useEffect(() => {
    if (!eventsLoaded) {
      return;
    }

    if (eventFromUrl && assignedEventIds.has(toId(eventFromUrl))) {
      setSelectedEvent(toId(eventFromUrl));
      return;
    }

    if (eventFromUrl && !assignedEventIds.has(toId(eventFromUrl))) {
      setSearchParams({}, { replace: true });
    }
    setSelectedEvent("");
  }, [eventsLoaded, eventFromUrl, assignedEventIds, setSearchParams]);

  useEffect(() => {
    if (!token || !eventsLoaded) {
      return;
    }
    // Wait until empty (clear) or at least 3 chars after debounce.
    if (debouncedSearch.length > 0 && debouncedSearch.length < 3) {
      return;
    }
    getList(1, selectedEvent, debouncedSearch);
  }, [token, eventsLoaded, selectedEvent, debouncedSearch, getList]);

  useEffect(() => {
    if (!error) {
      return;
    }
    if (toast.isActive(error) === false) {
      showErrorToast(error);
    }
    if (error === "You are unAuthrised") {
      goToGuardLogin();
    }
  }, [error, goToGuardLogin]);

  const handleSelect = (e) => {
    const value = toId(e.target.value);
    setSelectedEvent(value);
    if (value) {
      setSearchParams({ event: value });
    } else {
      setSearchParams({});
    }
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const filterElement = (
    <div className="event-attendees-filter-field">
      <label className="visually-hidden" htmlFor="guardAttendeeEventFilter">
        Event name
      </label>
      <select
        id="guardAttendeeEventFilter"
        className="event-attendees-event-select"
        value={selectedEvent}
        onChange={handleSelect}
        aria-label="Filter by event"
      >
        <option value="">All events</option>
        {events.map((ev) => {
          const id = toId(ev._id);
          return (
            <option key={id} value={id}>
              {ev.title || "Untitled event"}
            </option>
          );
        })}
      </select>
    </div>
  );

  return (
    <GuardLayout
      Heading={`Attendees List (${totalAttendeesCount})`}
      TitleMark={true}
      TitleMarkClassName="event-attendees-title-mark"
      TitleClassName="event-attendees-title"
      HeadClassName="event-page-head event-create-head event-create-head--split event-attendees-head"
      LeftClassName="event-create-head-left"
      CreateHead
      ExtraclassName="event-attendees-panel"
      extraElement={filterElement}
    >
      <MainComponentWrapper errorInfo={error} Loading={loading || !eventsLoaded}>
        <div className="event-attendees-body">
          {eventsLoaded && events.length === 0 ? (
            <div className="event-attendees-empty">
              You are not assigned to any event yet.
            </div>
          ) : attendeeList !== null ? (
            <Attendee
              isLoading={loading}
              isError={error}
              list={attendeeList}
              getList={getList}
              handleSearchChange={handleSearchChange}
              searchInput={searchInput}
            />
          ) : (
            <div className="event-attendees-empty">No attendees found</div>
          )}
        </div>
      </MainComponentWrapper>
    </GuardLayout>
  );
};

export default GuardEventPage;
